import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { etiquetaTotal } from 'shared/etiquetaUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Carrito distribuido por sucursal para clientes corporativos.
// Estructura: { [sucursalId]: [ { codigo, nombre, precio, cantidad, pesable, ... } ] }
const cartKeyFor = (sessionId) => `agorax_cart_corp_${sessionId}`;
const grupoKeyFor = (sessionId) => `agorax_grupo_corp_${sessionId}`;

function totalDeItems(items) {
  return (items || []).reduce((s, c) => {
    const base = c.pesable ? (c.precio * c.cantidad) / 1000 : c.precio * c.cantidad;
    return s + base + etiquetaTotal(c);
  }, 0);
}

function toProductos(items) {
  return (items || []).map((c) => ({
    codigo: c.codigo,
    nombre: c.nombre,
    precio: c.precio,
    cantidad: c.cantidad,
    pesable: c.pesable || false,
    imagen_url: c.imagen_url || '',
    etiquetas: (c.etiquetas || []).map((e) => ({ id: e.id, nombre: e.nombre, precio: e.precio || 0 })),
    etiquetas_extra: c.etiquetas_extra || 0,
    combo_id: c.combo_id || null,
    componentes: c.componentes || null,
  }));
}

export function useCartCorporativo({
  sessionId,
  schemaName,
  sedes = [],
  tasaCambio = 0,
  mostrarToast,
  onPedidoCreado,
  onConfirmarPedido,
}) {
  const [cartCorp, setCartCorp] = useState(() => {
    if (!sessionId) return {};
    try {
      return JSON.parse(localStorage.getItem(cartKeyFor(sessionId)) || '{}');
    } catch {
      return {};
    }
  });
  const [grupo, setGrupo] = useState(() => {
    if (!sessionId) return null;
    try {
      return JSON.parse(localStorage.getItem(grupoKeyFor(sessionId)) || 'null');
    } catch {
      return null;
    }
  });
  const [creandoPedido, setCreandoPedido] = useState(false);
  const cartRef = useRef(cartCorp);
  cartRef.current = cartCorp;
  const grupoRef = useRef(grupo);
  grupoRef.current = grupo;

  useEffect(() => {
    if (!sessionId) return;
    localStorage.setItem(cartKeyFor(sessionId), JSON.stringify(cartCorp));
  }, [cartCorp, sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    if (grupo) localStorage.setItem(grupoKeyFor(sessionId), JSON.stringify(grupo));
    else localStorage.removeItem(grupoKeyFor(sessionId));
  }, [grupo, sessionId]);

  const cantidadTotalProducto = useCallback((codigo) => {
    const cart = cartRef.current;
    return Object.values(cart).reduce(
      (s, items) => s + (items || []).filter((c) => c.codigo === codigo).reduce((a, c) => a + (c.cantidad || 0), 0),
      0
    );
  }, []);

  const crearGrupo = useCallback(async () => {
    const carrito = cartRef.current;
    const sedesConItems = (sedes || [])
      .map((s) => {
        const id = s.id;
        const items = carrito[id] || [];
        if (items.length === 0) return null;
        return { sucursal_id: id, productos: toProductos(items) };
      })
      .filter(Boolean);

    if (sedesConItems.length === 0) {
      mostrarToast('Agrega productos a tu pedido primero');
      return null;
    }

    const ubicacion = JSON.parse(localStorage.getItem(`agorax_ubicacion_${schemaName}`) || '{}');
    const res = await axios.post(`${API_URL}/pedidos/corporativo/iniciar`, {
      session_id: sessionId,
      schema_name: schemaName,
      lat: ubicacion.lat,
      lng: ubicacion.lng,
      direccion_texto: ubicacion.direccion || 'Ubicacion GPS',
      sedes: sedesConItems,
    });
    if (res.data.status === 'success') {
      const data = res.data.data;
      const pedidos = {};
      (data.pedidos || []).forEach((p) => {
        if (p.sucursal_id != null) pedidos[p.sucursal_id] = p.id;
      });
      const g = { grupo_cobro: data.grupo_cobro, pedidos };
      grupoRef.current = g;
      setGrupo(g);
      return g;
    }
    mostrarToast(res.data.message || 'Error al crear el pedido');
    return null;
  }, [sedes, sessionId, schemaName, mostrarToast]);

  const sincronizar = useCallback(async () => {
    const g = grupoRef.current;
    if (!g || !g.grupo_cobro) return { ok: true, stale: false };
    const carrito = cartRef.current;

    for (const sucursalId of Object.keys(carrito)) {
      const items = carrito[sucursalId] || [];
      if (items.length === 0) continue;

      let pedidoId = g.pedidos ? g.pedidos[sucursalId] : null;

      if (!pedidoId) {
        try {
          const res = await axios.post(`${API_URL}/pedidos/corporativo/${g.grupo_cobro}/sucursal`, {
            session_id: sessionId,
            schema_name: schemaName,
            sucursal_id: Number(sucursalId),
            productos: toProductos(items),
          });
          if (res.data.status === 'success') {
            pedidoId = res.data.data.pedido_id;
            const pedidos = { ...(g.pedidos || {}), [sucursalId]: pedidoId };
            const ng = { ...g, pedidos };
            grupoRef.current = ng;
            setGrupo(ng);
          } else {
            mostrarToast(res.data.message || 'Error al sincronizar la sucursal');
            continue;
          }
        } catch {
          mostrarToast('Error de conexión al sincronizar el pedido');
          continue;
        }
      }

      try {
        const res = await axios.put(`${API_URL}/pedidos/${pedidoId}/sync-cart`, {
          schema_name: schemaName,
          productos: toProductos(items),
          total: totalDeItems(items),
        });
        if (res.data.status === 'error') {
          const msg = res.data.message || '';
          if (msg.indexOf('no se puede modificar') !== -1) {
            return { ok: false, stale: true };
          }
          mostrarToast(msg);
          return { ok: false, stale: false };
        }
      } catch {
        return { ok: false, stale: false };
      }
    }
    return { ok: true, stale: false };
  }, [sessionId, schemaName, mostrarToast]);

  const limpiarGrupo = useCallback(() => {
    grupoRef.current = null;
    setGrupo(null);
  }, []);

  const agregarProducto = useCallback(
    async (prod, cantidadesPorSede, etiquetasSeleccionadas = []) => {
      const etiqSel = etiquetasSeleccionadas || [];

      // Validacion de stock agregada: la suma de todas las sucursales no puede
      // superar el stock real del articulo.
      if (!prod.combo_id) {
        const stockMax = prod.pesable ? (prod.stock || 0) * 1000 : (prod.stock || 0);
        const existente = cantidadTotalProducto(prod.codigo);
        const nueva = Object.values(cantidadesPorSede || {}).reduce((s, c) => s + (Number(c) || 0), 0);
        if (existente + nueva > stockMax) {
          const disp = prod.pesable ? (stockMax / 1000).toFixed(2) + ' kg' : stockMax + ' und';
          mostrarToast(`No hay suficiente stock de ${prod.descripcion || prod.nombre}. Disponible: ${disp}`);
          return false;
        }
      }

      const prev = cartRef.current;
      const nuevo = { ...prev };
      Object.entries(cantidadesPorSede || {}).forEach(([sucursalId, cant]) => {
        const cantidad = Number(cant) || 0;
        if (cantidad <= 0) return;
        const items = [...(nuevo[sucursalId] || [])];
        const etiqIdsKey = etiqSel.map((e) => e.id).sort().join(',');
        const idx = items.findIndex((c) => {
          if (c.codigo !== prod.codigo) return false;
          const cKey = (c.etiquetas || []).map((e) => e.id).sort().join(',');
          return cKey === etiqIdsKey;
        });
        if (idx >= 0) {
          const p = items[idx];
          items[idx] = {
            ...p,
            cantidad: p.cantidad + cantidad,
            etiquetas: etiqSel,
            etiquetas_extra: etiqSel.reduce((s, e) => s + (e.precio || 0), 0) * (p.cantidad + cantidad),
          };
        } else {
          items.push({
            codigo: prod.codigo,
            nombre: prod.descripcion || prod.nombre,
            precio: prod.precio,
            precio_bs: tasaCambio ? prod.precio * tasaCambio : 0,
            cantidad,
            stock: prod.stock,
            pesable: prod.pesable || false,
            imagen_url: prod.imagen_url || '',
            departamento: prod.departamento,
            etiquetas: etiqSel,
            etiquetas_extra: etiqSel.reduce((s, e) => s + (e.precio || 0), 0) * cantidad,
            combo_id: prod.combo_id || null,
            componentes: prod.combo_id ? prod.componentes : undefined,
          });
        }
        nuevo[sucursalId] = items;
      });
      cartRef.current = nuevo;
      setCartCorp(nuevo);

      // Iniciar el pedido con el primer articulo agregado.
      if (!grupoRef.current || !grupoRef.current.grupo_cobro) {
        setCreandoPedido(true);
        try {
          const g = await crearGrupo();
          if (!g) return false;
          if (onPedidoCreado) onPedidoCreado();
        } finally {
          setCreandoPedido(false);
        }
        return true;
      }

      const r = await sincronizar();
      if (r.stale) {
        limpiarGrupo();
        setCreandoPedido(true);
        try {
          const g = await crearGrupo();
          if (!g) return false;
          if (onPedidoCreado) onPedidoCreado();
        } finally {
          setCreandoPedido(false);
        }
        return true;
      }
      return r.ok;
    },
    [tasaCambio, cantidadTotalProducto, crearGrupo, sincronizar, limpiarGrupo, onPedidoCreado, mostrarToast]
  );

  const actualizarCantidadSede = useCallback(
    async (sucursalId, idx, delta) => {
      const prev = cartRef.current;
      const items = [...(prev[sucursalId] || [])];
      const c = items[idx];
      if (!c) return;
      if (delta > 0 && !c.combo_id) {
        const stockMax = c.pesable ? (c.stock || 0) * 1000 : (c.stock || 0);
        const existente = cantidadTotalProducto(c.codigo);
        if (existente + delta > stockMax) {
          mostrarToast(`No hay suficiente stock de ${c.nombre}. Disponible: ${stockMax}`);
          return;
        }
      }
      const nueva = c.cantidad + delta;
      if (nueva <= 0) {
        items.splice(idx, 1);
      } else {
        items[idx] = { ...c, cantidad: nueva, etiquetas_extra: c.combo_id ? c.etiquetas_extra : etiquetaTotal(c) };
      }
      const nuevo = { ...prev, [sucursalId]: items };
      cartRef.current = nuevo;
      setCartCorp(nuevo);
      if (grupoRef.current && grupoRef.current.grupo_cobro) await sincronizar();
    },
    [cantidadTotalProducto, sincronizar, mostrarToast]
  );

  const eliminarDeSede = useCallback(
    async (sucursalId, idx) => {
      const prev = cartRef.current;
      const items = [...(prev[sucursalId] || [])];
      items.splice(idx, 1);
      const nuevo = { ...prev, [sucursalId]: items };
      cartRef.current = nuevo;
      setCartCorp(nuevo);
      if (grupoRef.current && grupoRef.current.grupo_cobro) await sincronizar();
    },
    [sincronizar]
  );

  const vaciar = useCallback(() => {
    cartRef.current = {};
    setCartCorp({});
    grupoRef.current = null;
    setGrupo(null);
    if (sessionId) {
      localStorage.removeItem(cartKeyFor(sessionId));
      localStorage.removeItem(grupoKeyFor(sessionId));
    }
  }, [sessionId]);

  const totalGeneral = Object.values(cartCorp).reduce((s, items) => s + totalDeItems(items), 0);
  const contarItems = Object.values(cartCorp).reduce((s, items) => s + items.length, 0);

  const totalPorSede = useCallback(
    (sucursalId) => totalDeItems(cartCorp[sucursalId] || []),
    [cartCorp]
  );

  const confirmarPedido = useCallback(async () => {
    if (Object.keys(cartRef.current).length === 0) {
      mostrarToast('Agrega productos a tu pedido primero');
      return;
    }
    setCreandoPedido(true);
    try {
      await sincronizar();
      const g = grupoRef.current;
      const primerId = g && g.pedidos ? Object.values(g.pedidos)[0] : null;
      if (onConfirmarPedido) onConfirmarPedido(primerId || null);
    } finally {
      setCreandoPedido(false);
    }
  }, [sincronizar, onConfirmarPedido, mostrarToast]);

  return {
    cartCorp,
    setCartCorp,
    grupo,
    creandoPedido,
    agregarProducto,
    actualizarCantidadSede,
    eliminarDeSede,
    totalPorSede,
    totalGeneral,
    contarItems,
    confirmarPedido,
    vaciar,
  };
}
